import { completeClock, toClock } from '@/stores/shiftDraftModel';
import type { ShiftApiViolationDetail } from '@/services/shiftsApi';
import type {
  ShiftAccrual,
  ShiftAccrualKind,
  ShiftDetail,
  ShiftHeader,
  ShiftMember,
  ShiftMemberStatus,
  ShiftStatus,
  UpdateShiftPayload,
} from '@/types/shift.types';
import { formatIsoClock } from '../ShiftsWeek/shiftWeekModel';

export const SHIFT_VIEW_STATUS_LABEL: Record<ShiftStatus, string> = {
  created: 'Создана',
  open: 'Открыта',
  closed: 'Закрыта',
};

export const MEMBER_STATUS_LABEL: Record<ShiftMemberStatus, string> = {
  assigned: 'Назначен',
  on_shift: 'На смене',
  no_show: 'Не вышел',
  worked: 'Отработал',
};

export type MemberMenuAction =
  | 'start'
  | 'finish'
  | 'no_show'
  | 'return'
  | 'change_time'
  | 'remove';

export interface MemberMenuItem {
  action: MemberMenuAction;
  label: string;
}

const ASSIGNED_MENU: MemberMenuItem[] = [
  { action: 'start', label: 'Начать работу' },
  { action: 'no_show', label: 'Не вышел' },
  { action: 'change_time', label: 'Изменить время' },
  { action: 'remove', label: 'Удалить из смены' },
];

const ON_SHIFT_MENU: MemberMenuItem[] = [
  { action: 'finish', label: 'Завершить работу' },
  { action: 'change_time', label: 'Изменить время' },
  { action: 'remove', label: 'Удалить из смены' },
];

const NO_SHOW_MENU: MemberMenuItem[] = [
  { action: 'start', label: 'Отметить вышедшим' },
  { action: 'remove', label: 'Удалить из смены' },
];

const WORKED_MENU: MemberMenuItem[] = [
  { action: 'change_time', label: 'Изменить время' },
  { action: 'return', label: 'Вернуть на смену' },
];

const memberMenuForStatus = (status: ShiftMemberStatus): MemberMenuItem[] => {
  if (status === 'assigned') return ASSIGNED_MENU;
  if (status === 'on_shift') return ON_SHIFT_MENU;
  if (status === 'no_show') return NO_SHOW_MENU;
  return WORKED_MENU;
};

/**
 * На закрытой смене — только фактическое время.
 * «Удалить из смены» только пока смена «Создана» (строка реально снимается).
 * На открытой смене пункт дублировал «Не вышел» или давал 409.
 */
export const memberMenu = (
  status: ShiftMemberStatus,
  shiftStatus?: ShiftStatus,
): MemberMenuItem[] => {
  let items = memberMenuForStatus(status);
  if (shiftStatus === 'closed') {
    return items.filter((item) => item.action === 'change_time');
  }
  if (shiftStatus === 'open') {
    items = items.filter((item) => item.action !== 'remove');
  }
  return items;
};

export const shiftTagColor = (status: ShiftStatus): 'default' | 'green' =>
  status === 'open' ? 'green' : 'default';

export const memberTagColor = (status: ShiftMemberStatus): 'default' | 'green' =>
  status === 'on_shift' || status === 'worked' ? 'green' : 'default';

export const memberInitials = (name: string): string =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');

const clockLabel = (value: string | null): string | null => {
  if (!value) return null;
  const match = /^(\d{2}):(\d{2})/.exec(value);
  return match ? `${match[1]}:${match[2]}` : null;
};

const joinInterval = (start: string | null, end: string | null): string => {
  if (start && end) return `${start}–${end}`;
  return start ?? end ?? '';
};

/** Фактический интервал, если он уже записан. Иначе плановый. */
export const memberIntervalLabel = (
  member: Pick<ShiftMember, 'actual_start' | 'actual_end' | 'planned_start' | 'planned_end'>,
  timeZone: string,
): string => {
  if (member.actual_start || member.actual_end) {
    return joinInterval(
      member.actual_start ? formatIsoClock(member.actual_start, timeZone) : null,
      member.actual_end ? formatIsoClock(member.actual_end, timeZone) : null,
    );
  }
  return joinInterval(clockLabel(member.planned_start), clockLabel(member.planned_end));
};

export const memberClockValue = (
  actual: string | null,
  planned: string | null,
  timeZone: string,
): string => {
  if (actual) return formatIsoClock(actual, timeZone) ?? '';
  return clockLabel(planned) ?? '';
};

/** До закрытия header — часы центра, после — фактический интервал смены. */
export const headerIntervalLabel = (header: ShiftHeader, timeZone: string): string =>
  joinInterval(
    formatIsoClock(header.start, timeZone),
    formatIsoClock(header.end, timeZone),
  );

const ACCRUAL_KINDS: ShiftAccrualKind[] = ['attendance', 'hours', 'services'];

const ACCRUAL_LABEL: Record<ShiftAccrualKind, string> = {
  attendance: 'За выход',
  hours: 'Часы',
  services: 'Услуги',
};

export interface AccrualDisplayRow {
  kind: ShiftAccrualKind;
  label: string;
  amount: string;
}

const toKopecks = (value: string): number => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return 0;
  return Math.round(numeric * 100);
};

const fromKopecks = (value: number): string => (value / 100).toFixed(2);

/** Три строки макета: суммы уже посчитанных сервером lines, без пересчёта ставок. */
export const accrualRows = (accrual: ShiftAccrual): AccrualDisplayRow[] =>
  ACCRUAL_KINDS.map((kind) => {
    const kopecks = accrual.lines
      .filter((line) => line.kind === kind)
      .reduce((sum, line) => sum + toKopecks(line.amount), 0);
    return {
      kind,
      label: ACCRUAL_LABEL[kind],
      amount: fromKopecks(kopecks),
    };
  });

export const formatAccrualAmount = (value: string): string => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return '—';
  const negative = numeric < 0;
  const [whole, fraction] = Math.abs(numeric).toFixed(2).split('.');
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  const body = fraction === '00' ? grouped : `${grouped},${fraction}`;
  return `${negative ? '−' : ''}${body} ₽`;
};

export const employeeCountLabel = (count: number): string => {
  const mod10 = count % 10;
  const mod100 = count % 100;
  if (mod10 === 1 && mod100 !== 11) return `${count} сотрудник`;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return `${count} сотрудника`;
  return `${count} сотрудников`;
};

export const accrualTitle = (status: ShiftStatus): string | null => {
  if (status === 'open') return 'Предварительные начисления';
  if (status === 'closed') return 'Начисления за смену';
  return null;
};

const CLOSE_VIOLATION_MESSAGE: Record<string, string> = {
  blocking_bookings:
    'На эту дату есть незавершённые заявки. Завершите или отмените их перед закрытием смены.',
  completed_without_performers: 'У завершённой заявки на эту дату не указан исполнитель.',
  completed_performers_not_worked:
    'Исполнитель завершённой заявки должен быть в смене со статусом «Отработал».',
  responsible_missing: 'Укажите ответственного за смену.',
  responsible_not_in_roster: 'Ответственный должен входить в состав смены.',
};

const isTechnicalMembersCloseMessage = (message: string): boolean =>
  /assigned|on_shift|worked не заполнено|members_not_finished/i.test(message);

const isTechnicalActionMessage = (message: string): boolean =>
  /assigned|on_shift|member_uuids|members_not_finished|SHIFT_|Conflict/i.test(message);

/** Баннер после мутации строки: читаемый текст с сервера, без технических кодов. */
export const userFacingMutationError = (
  parsed: { message: string; details: string[] },
  fallback: string,
): string => {
  for (const text of [...parsed.details, parsed.message]) {
    if (text && !isTechnicalActionMessage(text)) return text;
  }
  return fallback;
};

const memberBlocksShiftClose = (member: ShiftMember): boolean => {
  if (member.status === 'assigned' || member.status === 'on_shift') return true;
  if (member.status !== 'worked') return false;
  if (!member.actual_start || !member.actual_end) return true;
  const start = new Date(member.actual_start).getTime();
  const end = new Date(member.actual_end).getTime();
  return Number.isNaN(start) || Number.isNaN(end) || end <= start;
};

const memberCloseViolationMessage = (member: ShiftMember): string => {
  if (member.status === 'assigned') {
    return `${member.name}: начните и завершите работу или отметьте «Не вышел»`;
  }
  if (member.status === 'on_shift') {
    return `${member.name}: завершите работу`;
  }
  if (!member.actual_start || !member.actual_end) {
    return `${member.name}: укажите фактическое время начала и конца`;
  }
  return `${member.name}: укажите корректное фактическое время (конец позже начала)`;
};

const membersCloseViolationMessages = (
  detail: ShiftDetail | null,
  memberUuids?: string[],
): string[] => {
  const members = detail?.members ?? [];
  const targets = memberUuids?.length
    ? members.filter((member) => memberUuids.includes(member.uuid))
    : members.filter(memberBlocksShiftClose);
  if (targets.length === 0) {
    return ['Завершите работу всех сотрудников и укажите фактическое время у отработавших'];
  }
  return targets.map(memberCloseViolationMessage);
};

/** Человекочитаемые причины 409 при закрытии смены. */
export const formatCloseViolationMessages = (
  detail: ShiftDetail | null,
  violations: ShiftApiViolationDetail[],
  fallbackDetails: string[] = [],
): string[] => {
  if (violations.length > 0) {
    return violations.flatMap((violation) => {
      if (violation.code === 'members_not_finished') {
        return membersCloseViolationMessages(detail, violation.member_uuids);
      }
      if (violation.code && CLOSE_VIOLATION_MESSAGE[violation.code]) {
        return [CLOSE_VIOLATION_MESSAGE[violation.code]];
      }
      if (violation.message && isTechnicalMembersCloseMessage(violation.message)) {
        return membersCloseViolationMessages(detail, violation.member_uuids);
      }
      return violation.message ? [violation.message] : [];
    });
  }

  return fallbackDetails.flatMap((message) => {
    if (isTechnicalMembersCloseMessage(message)) {
      return membersCloseViolationMessages(detail);
    }
    if (message === 'Не задан ответственный') {
      return [CLOSE_VIOLATION_MESSAGE.responsible_missing];
    }
    if (message === 'Ответственный не входит в состав смены') {
      return [CLOSE_VIOLATION_MESSAGE.responsible_not_in_roster];
    }
    return [message];
  });
};

const clockToMinutes = (value: string): number | null => {
  const match = /^(\d{2}):(\d{2})$/.exec(completeClock(value));
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) return null;
  return hours * 60 + minutes;
};

/**
 * Плановое время: смена ещё создана, либо сотрудник назначен на уже открытую.
 * Фактическое время сервер принимает только у «На смене» и «Отработал».
 */
export const savesPlannedMemberTime = (
  shiftStatus: ShiftStatus,
  memberStatus: ShiftMemberStatus,
): boolean =>
  memberStatus === 'assigned' && (shiftStatus === 'created' || shiftStatus === 'open');

/** Полный состав из detail; меняются только planned_start/planned_end выбранного участника. */
export const plannedTimeUpdatePayload = (
  detail: Pick<ShiftDetail, 'responsible_employee_uuid' | 'members'>,
  memberUuid: string,
  start: string,
  end: string,
): UpdateShiftPayload => ({
  responsible_employee_uuid: detail.responsible_employee_uuid,
  members: detail.members.map((member) => ({
    employee_uuid: member.employee_uuid,
    planned_start:
      member.uuid === memberUuid ? completeClock(start) : toClock(member.planned_start),
    planned_end: member.uuid === memberUuid ? completeClock(end) : toClock(member.planned_end),
    payment_scheme_template_uuid: member.payment_scheme_template_uuid,
  })),
});

/** Оба поля обязательны, конец позже начала. Пустая строка — ошибка. */
export const validateActualClocks = (start: string, end: string): string | null => {
  const startMinutes = clockToMinutes(start);
  const endMinutes = clockToMinutes(end);
  if (startMinutes === null || endMinutes === null) return 'Укажите время начала и конца';
  if (endMinutes <= startMinutes) return 'Конец должен быть позже начала';
  return null;
};

const timeZoneOffsetMs = (instant: Date, timeZone: string): number => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(instant);
  const pick = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((part) => part.type === type)?.value);
  const asUtc = Date.UTC(
    pick('year'),
    pick('month') - 1,
    pick('day'),
    pick('hour'),
    pick('minute'),
    pick('second'),
  );
  return asUtc - instant.getTime();
};

/** Часы пикера на дату смены в поясе центра → ISO, который ждёт actual-time. */
export const clockOnDateToIso = (
  date: string,
  clock: string,
  timeZone: string,
): string | null => {
  const dateMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  const normalized = completeClock(clock);
  const clockMatch = /^(\d{2}):(\d{2})$/.exec(normalized);
  if (!dateMatch || !clockMatch) return null;

  const year = Number(dateMatch[1]);
  const month = Number(dateMatch[2]);
  const day = Number(dateMatch[3]);
  const hour = Number(clockMatch[1]);
  const minute = Number(clockMatch[2]);
  if (hour > 23 || minute > 59) return null;

  const zone = timeZone || 'Europe/Moscow';
  const utcGuess = Date.UTC(year, month - 1, day, hour, minute, 0);
  const offset = timeZoneOffsetMs(new Date(utcGuess), zone);
  const adjusted = timeZoneOffsetMs(new Date(utcGuess - offset), zone);
  return new Date(utcGuess - adjusted).toISOString();
};
